// Response cache for whole-list GETs (D-110). Switching Calendar Sync ↔ Financial Hub
// remounts every page and re-asked API Gateway for the same lists (14 hub reads + the
// calendar rows + reports + lookups), each serialised through the 4-in-flight gate.
// A list answered in the last API_CACHE_TTL_MS is served from memory instead; a write
// under the same route drops it, so the next read is fresh. Memory only (no storage):
// the cache dies with the tab and is cleared on sign-out. Never a timer, never a refresh
// loop — a hit is just a GET that did not need the network.
export const API_CACHE_TTL_MS = 5 * 60 * 1000;
export const API_CACHE_MAX_ENTRIES = 200;

// Exact GET paths (query string allowed) served from the cache. Per-record reads, job
// polls, drafts, audits and sync/status probes are never cached.
export const CACHED_GET_PATHS = [
  "/calendar-imports", "/calendar/status",
  "/claims", "/drugs", "/lookups", "/eobs", "/patients",
  "/corporate-import/reports", "/corporate-import/eob-rules", "/corporate-import/pending",
  "/pricing/master", "/pricing/inventory-state",
  "/inventory/lots", "/inventory/invoices",
  "/gl/accounts", "/fin-connections", "/financials",
  "/cashflow/transactions", "/cashflow/debts", "/deposits", "/fulfillment",
  "/finance-settings", "/perf-targets", "/valuation-settings",
];

// A write whose path does not share a first segment with the lists it changes.
const WRITE_ALIASES = { "/api/financial-hub/eob": ["/eobs"] };

const entries = new Map(); // path → { at, value }
const inFlight = new Map(); // path → Promise
const stats = { requests: 0, hits: 0, bytes: 0 };

const basePath = (path) => String(path || "").split("?")[0];
const firstSegment = (path) => { const m = /^\/[^/?]+/.exec(String(path || "")); return m ? m[0] : ""; };
export const isCacheablePath = (path) => CACHED_GET_PATHS.includes(basePath(path));

const clone = (v) => (v === undefined || v === null ? v : JSON.parse(JSON.stringify(v)));

export function apiCacheGet(path, now = Date.now()) {
  const e = entries.get(path);
  if (!e) return undefined;
  if (now - e.at > API_CACHE_TTL_MS) { entries.delete(path); return undefined; }
  return clone(e.value);
}

export function apiCacheSet(path, value, now = Date.now()) {
  if (entries.size >= API_CACHE_MAX_ENTRIES) {
    const oldest = entries.keys().next().value;
    if (oldest !== undefined) entries.delete(oldest);
  }
  entries.delete(path);
  entries.set(path, { at: now, value: clone(value) });
  try { stats.bytes += JSON.stringify(value).length; } catch (_) { /* unserialisable → uncounted */ }
}

/** Serve `path` from the cache, or run `load()` once and remember its answer (concurrent callers share the one request). */
export function cachedRead(path, load) {
  stats.requests += 1;
  const hit = apiCacheGet(path);
  if (hit !== undefined) { stats.hits += 1; return Promise.resolve(hit); }
  const pending = inFlight.get(path);
  if (pending) { stats.hits += 1; return pending.then(clone); }
  const p = Promise.resolve()
    .then(load)
    .then((value) => { apiCacheSet(path, value); return value; })
    .finally(() => { if (inFlight.get(path) === p) inFlight.delete(path); });
  inFlight.set(path, p);
  return p;
}

/** Drop every cached path under `prefix` (first segment match: "/claims" drops "/claims?x=1" too). */
export function invalidateApiCache(prefix) {
  if (!prefix) { entries.clear(); return; }
  for (const key of [...entries.keys()]) {
    if (key === prefix || key.startsWith(`${prefix}?`) || key.startsWith(`${prefix}/`)) entries.delete(key);
  }
}

/** A write to `path` changes whatever lists live under its first segment (plus aliases). */
export function invalidateForWrite(path) {
  const seg = firstSegment(path);
  if (seg) invalidateApiCache(seg);
  for (const [alias, targets] of Object.entries(WRITE_ALIASES)) {
    if (String(path || "").startsWith(alias)) targets.forEach(invalidateApiCache);
  }
}

export function clearApiCache() { entries.clear(); inFlight.clear(); }
export const apiCacheSize = () => entries.size;
/** No-PHI counters: GETs asked of the cache, how many never reached the network, bytes stored. */
export const apiStats = () => ({ ...stats, cacheEntries: entries.size });
export function resetApiStats() { stats.requests = 0; stats.hits = 0; stats.bytes = 0; }
